/**
 * Toolbar de filtros da página /casos (E13.F5.T01).
 *
 * Controlada por props vindas de `CasosPage` (que mantém o estado sincronizado
 * com URLSearchParams para deep-link). Renderiza:
 *   - inputs CPF (com máscara) e contrato (existentes pré-F5)
 *   - input CNJ (com máscara) — novo em F5
 *   - status multi-select via <details>/<summary>
 *   - select de espécime
 *   - intervalo de datas (De / Até)
 *   - chips de filtros ativos com botão X
 *   - botão "Limpar filtros" quando há filtros ativos
 */
import { useEffect, useId, useMemo, useRef, type ChangeEvent } from 'react';

import { CLASSES_CONTROLE } from '@/components/ui/Input';
import type { CaseStatus as BackendCaseStatus } from '@/lib/cases';
import { formatCnj, formatCpf } from '@/lib/masks';
import { cn } from '@/lib/utils';
import { ptBR } from '@/i18n/pt-BR';
import {
  computeActiveChips,
  STATUS_VALUES,
  type CasosFilterState,
} from '@/components/Casos/filters';

export {
  applyClientFilters,
  computeActiveChips,
  digitsOf,
  EMPTY_FILTERS,
  hasAnyFilter,
  STATUS_VALUES,
  type ActiveChip,
  type CasosFilterState,
} from '@/components/Casos/filters';

const ESPECIME_VALUES: readonly string[] = [
  'Contrato',
  'Cheque',
  'Procuração',
  'Testamento',
  'Aval em nota promissória',
  'Termo de quitação',
  'Endosso',
  'Outro',
];

function backendStatusLabel(status: BackendCaseStatus): string {
  if (status === 'draft') return ptBR.casos.statusDraft;
  if (status === 'analysing') return ptBR.casos.statusAnalysing;
  if (status === 'done') return ptBR.casos.statusDone;
  return ptBR.casos.statusConflict;
}

function especimeLabel(value: string): string {
  switch (value) {
    case 'Contrato':
      return ptBR.casos.especimeContrato;
    case 'Cheque':
      return ptBR.casos.especimeCheque;
    case 'Procuração':
      return ptBR.casos.especimeProcuracao;
    case 'Testamento':
      return ptBR.casos.especimeTestamento;
    case 'Aval em nota promissória':
      return ptBR.casos.especimeAval;
    case 'Termo de quitação':
      return ptBR.casos.especimeQuitacao;
    case 'Endosso':
      return ptBR.casos.especimeEndosso;
    case 'Outro':
      return ptBR.casos.especimeOutro;
    default:
      return value;
  }
}

interface CasosToolbarProps {
  filters: CasosFilterState;
  onPatch: (next: Partial<CasosFilterState>) => void;
  onClearAll: () => void;
  // E23.F24 — exibe o atalho "Aguardando minha homologação" só p/ quem revisa.
  canReview?: boolean;
}

export function CasosToolbar({
  filters,
  onPatch,
  onClearAll,
  canReview = false,
}: CasosToolbarProps): JSX.Element {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryId = useId();
  const statusListboxId = `${summaryId}-list`;

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (!detailsRef.current) return;
      if (!detailsRef.current.open) return;
      if (detailsRef.current.contains(event.target as Node)) return;
      detailsRef.current.open = false;
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  const statusSummaryLabel = useMemo(() => {
    if (filters.statuses.length === 0) return ptBR.casos.filters.statusPlaceholderNone;
    const first = filters.statuses[0];
    if (filters.statuses.length === 1 && first !== undefined) return backendStatusLabel(first);
    return ptBR.casos.filters.statusPlaceholderMany.replace(
      '{{count}}',
      String(filters.statuses.length),
    );
  }, [filters.statuses]);

  function toggleStatus(value: BackendCaseStatus) {
    const set = new Set(filters.statuses);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onPatch({ statuses: STATUS_VALUES.filter((s) => set.has(s)) });
  }

  function handleCpf(e: ChangeEvent<HTMLInputElement>) {
    onPatch({ cpf: formatCpf(e.target.value) });
  }
  function handleCnj(e: ChangeEvent<HTMLInputElement>) {
    onPatch({ cnj: formatCnj(e.target.value) });
  }

  const chips = computeActiveChips(filters, onPatch);

  return (
    <div className="flex flex-col gap-3" data-testid="casos-toolbar">
      {canReview && (
        <label className="flex w-fit items-center gap-2 text-body-sm text-fg">
          <input
            type="checkbox"
            checked={filters.awaitingMyReview}
            onChange={(e) => onPatch({ awaitingMyReview: e.target.checked })}
            data-testid="casos-filter-awaiting-my-review"
            className="h-4 w-4 border-border accent-accent-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          {ptBR.casos.reviewFilterMyReview}
        </label>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.search.cpfLabel}
          </span>
          <div className="relative">
            <input
              type="text"
              data-testid="casos-search-cpf"
              inputMode="numeric"
              maxLength={14}
              autoComplete="off"
              placeholder={ptBR.casos.search.cpfPlaceholder}
              value={filters.cpf}
              onChange={handleCpf}
              className={cn(CLASSES_CONTROLE, 'pr-8 font-mono')}
            />
            {filters.cpf !== '' ? (
              <button
                type="button"
                data-testid="casos-search-cpf-clear"
                aria-label={ptBR.casos.search.clearAria}
                onClick={() => onPatch({ cpf: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-caption text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
              >
                ×
              </button>
            ) : null}
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.search.contratoLabel}
          </span>
          <div className="relative">
            <input
              type="text"
              data-testid="casos-search-contrato"
              autoComplete="off"
              maxLength={64}
              placeholder={ptBR.casos.search.contratoPlaceholder}
              value={filters.contrato}
              onChange={(e) => onPatch({ contrato: e.target.value })}
              className={cn(CLASSES_CONTROLE, 'pr-8')}
            />
            {filters.contrato !== '' ? (
              <button
                type="button"
                data-testid="casos-search-contrato-clear"
                aria-label={ptBR.casos.search.clearAria}
                onClick={() => onPatch({ contrato: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-caption text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
              >
                ×
              </button>
            ) : null}
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.search.cnjLabel}
          </span>
          <input
            type="text"
            data-testid="casos-search-cnj"
            inputMode="numeric"
            maxLength={25}
            autoComplete="off"
            placeholder={ptBR.casos.search.cnjPlaceholder}
            value={filters.cnj}
            onChange={handleCnj}
            className={cn(CLASSES_CONTROLE, 'font-mono')}
          />
        </label>

        {/*
          O rótulo sai de dentro do `<summary>` (E54.F4.T04): lá dentro ele
          empurrava a caixa para uma altura maior que a dos outros controles e
          o campo saía da linha de base da grade de filtros da prancha 1B.
          Fora, o controle tem a mesma altura mínima dos demais.
        */}
        <div className="flex flex-col">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.filters.statusLabel}
          </span>
          <details ref={detailsRef} className="relative" data-testid="casos-filter-status">
            <summary
              id={summaryId}
              className="flex min-h-9 cursor-pointer list-none items-center border border-border bg-surface px-2.5 py-1.5 text-[14px] text-fg transition-colors duration-fast ease-sc hover:border-fg/45 focus-visible:outline-2 focus-visible:outline-accent"
              aria-haspopup="listbox"
              aria-controls={statusListboxId}
              aria-label={ptBR.casos.filters.statusOpen}
            >
              <span data-testid="casos-filter-status-summary">{statusSummaryLabel}</span>
            </summary>
            <ul
              id={statusListboxId}
              role="listbox"
              aria-multiselectable
              className="absolute left-0 right-0 top-full z-10 mt-1 flex flex-col gap-1 border border-border bg-surface p-2 shadow-md"
            >
              {STATUS_VALUES.map((s) => {
                const checked = filters.statuses.includes(s);
                return (
                  <li key={s} role="option" aria-selected={checked}>
                    <label className="flex cursor-pointer items-center gap-2 px-2 py-1 text-body-sm text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.05]">
                      <input
                        type="checkbox"
                        data-testid={`casos-filter-status-${s}`}
                        checked={checked}
                        onChange={() => toggleStatus(s)}
                        className="h-4 w-4 border-border accent-accent-600"
                      />
                      <span>{backendStatusLabel(s)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.filters.especimeLabel}
          </span>
          <select
            data-testid="casos-filter-especime"
            value={filters.especime}
            onChange={(e) => onPatch({ especime: e.target.value })}
            className={CLASSES_CONTROLE}
          >
            <option value="">{ptBR.casos.filters.especimePlaceholder}</option>
            {ESPECIME_VALUES.map((value) => (
              <option key={value} value={value}>
                {especimeLabel(value)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.filters.dateFromLabel}
          </span>
          <input
            type="date"
            data-testid="casos-filter-date-from"
            value={filters.dateFrom}
            onChange={(e) => onPatch({ dateFrom: e.target.value })}
            className={CLASSES_CONTROLE}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="mb-[5px] block text-caption text-fg-dim">
            {ptBR.casos.filters.dateToLabel}
          </span>
          <input
            type="date"
            data-testid="casos-filter-date-to"
            value={filters.dateTo}
            onChange={(e) => onPatch({ dateTo: e.target.value })}
            className={CLASSES_CONTROLE}
          />
        </label>
      </div>

      {chips.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="casos-active-chips"
          aria-label={ptBR.casos.filters.activeChipsLabel}
        >
          {chips.map((c) => (
            <button
              type="button"
              key={c.key}
              data-testid={`casos-chip-${c.key}`}
              onClick={c.onRemove}
              className="inline-flex items-center gap-1 border border-border bg-transparent px-2.5 py-[3px] text-[11px] tracking-[0.02em] text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-accent"
              aria-label={c.removeAria}
            >
              <span>{c.label}</span>
              <span aria-hidden className="text-fg-dim">
                ×
              </span>
            </button>
          ))}
          <button
            type="button"
            data-testid="casos-clear-all"
            onClick={onClearAll}
            className="border border-transparent px-2 py-1 text-caption text-accent-700 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {ptBR.casos.filters.clearAll}
          </button>
        </div>
      ) : null}
    </div>
  );
}
